import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Recycle,
  Users,
  MessageSquare,
  BarChart3,
  QrCode,
  ArrowRight,
  ArrowUp,
  Mail,
  ChevronDown,
  Shield,
  Clock,
  Leaf,
  Package,
  Layers,
  ShieldAlert,
  Play,
  Bell,
  Sparkles,
  Truck,
  CalendarDays,
} from "lucide-react";
import ResoviaLogo from "../components/ResoviaLogo";

const FEATURES = [
  {
    icon: MapPin,
    title: "Interaktivt Kort",
    desc: "Styr ressourceøer, zoner, boder og arbejdsområder visuelt på dit eget festivalkort med realtidsstatus og GPS-tracking af køretøjer.",
  },
  {
    icon: Recycle,
    title: "Tømning & Affaldssporing",
    desc: "Bestil tømninger med antal, følg flowet Bestil → På vej → Tømt, og registrér ekstra tømninger direkte i marken. Alt logges til statistik.",
  },
  {
    icon: QrCode,
    title: "QR & Quick Check-in",
    desc: "Scan en QR på ressourceøen og bestil tømning på sekunder. Frivillige tjekker ind med QR pr. team og logges automatisk ud efter vagten.",
  },
  {
    icon: Bell,
    title: "Push-notifikationer",
    desc: "Rigtige notifikationer direkte på telefonen — beskeder, kalenderopgaver og rengøringszoner rammer de rigtige teams, også når appen er lukket.",
  },
  {
    icon: Users,
    title: "Frivillige & Vagtplaner",
    desc: "Importér frivillige fra Excel, organisér teams med præcise rettigheder og fordel vagter jævnt over hele pladsen.",
  },
  {
    icon: CalendarDays,
    title: "Kalender & Faser",
    desc: "Planlæg opgaver og deadlines i kalenderen med team-notifikationer, og følg fremdriften gennem opbygning, afvikling og nedpakning.",
  },
  {
    icon: MessageSquare,
    title: "Kommunikation",
    desc: "Indbygget besked- og notifikationssystem til koordinering mellem teams, ledere og frivillige — med planlagte beskeder.",
  },
  {
    icon: BarChart3,
    title: "Analyse & Live Monitor",
    desc: "Følg tømninger, sorteringskvalitet og fasefremdrift i realtid, og generér rapporter du kan bruge til at optimere næste event.",
  },
  {
    icon: Sparkles,
    title: "AI Evaluering",
    desc: "Tal din evaluering ind — AI'en transskriberer og samler pointerne, så erfaringerne bliver fastholdt til næste år.",
  },
  {
    icon: Package,
    title: "Materiel & Lager",
    desc: "Bestil containere, skiltning og udstyr direkte fra systemet. Hold styr på lagerbeholdning, forbrug og leverancer.",
  },
  {
    icon: Layers,
    title: "Flere Events — Én Platform",
    desc: "Administrér alle dine events fra samme app. Skift nemt mellem arrangementer, genbrug opsætninger og saml data på tværs.",
  },
  {
    icon: ShieldAlert,
    title: "Sikkerhed",
    desc: "Markér evakueringspunkter, nødudgange og sikkerhedszoner direkte på kortet, og del kritisk information i realtid.",
  },
];

const VALUES = [
  {
    icon: Leaf,
    title: "Bæredygtighed",
    text: "Designet til grønnere events med datadrevet affaldsreduktion.",
  },
  {
    icon: Shield,
    title: "Overblik & Tryghed",
    text: "Skaber sikkerhed og kontrol for dem, der håndterer ressourcerne.",
  },
  {
    icon: Clock,
    title: "Effektivitet",
    text: "Automatisér logistik og frigør tid til det, der virkelig tæller.",
  },
];

/* One continuous organic SVG that flows from hero → features → values */
function FlowingVine() {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 1440 4200"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="vineGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#88C057" stopOpacity="0.25" />
          <stop offset="30%" stopColor="#5B8C2A" stopOpacity="0.18" />
          <stop offset="60%" stopColor="#88C057" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#5B8C2A" stopOpacity="0.12" />
        </linearGradient>
        <linearGradient id="vineGrad2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6BA33E" stopOpacity="0.12" />
          <stop offset="50%" stopColor="#88C057" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#5B8C2A" stopOpacity="0.08" />
        </linearGradient>
      </defs>

      {/* Main flowing ribbon — starts top-right, snakes down-left, back right, left again */}
      <path
        d="
          M 1500 -50
          C 1300 100, 1100 300, 1050 500
          C 980 750, 1100 850, 900 1100
          C 650 1400, 300 1300, 150 1600
          C -50 1950, 250 2100, 500 2300
          C 800 2550, 1100 2400, 1200 2700
          C 1300 3000, 900 3200, 600 3400
          C 300 3600, 100 3800, 0 4000
          L -100 4000
          L -100 3700
          C 100 3500, 300 3300, 500 3150
          C 750 2950, 1050 2800, 1000 2550
          C 940 2300, 600 2350, 350 2100
          C 50 1850, -100 1700, 100 1400
          C 300 1100, 600 1200, 800 950
          C 1000 700, 850 550, 900 350
          C 950 150, 1200 50, 1500 -50
          Z
        "
        fill="url(#vineGrad)"
      />

      {/* Thinner accent ribbon — offset, adds depth */}
      <path
        d="
          M 1500 100
          C 1250 250, 1150 400, 1100 600
          C 1030 850, 1050 950, 850 1200
          C 600 1500, 350 1400, 200 1700
          C 0 2050, 200 2200, 450 2400
          C 750 2650, 1050 2500, 1150 2800
          C 1250 3100, 850 3300, 550 3500
          C 250 3700, 50 3900, -50 4100
          L -100 4100
          L -100 3850
          C 50 3650, 250 3500, 450 3300
          C 700 3100, 1000 2900, 950 2650
          C 880 2400, 550 2450, 300 2200
          C 0 1950, -100 1800, 150 1500
          C 350 1200, 550 1300, 750 1050
          C 950 800, 900 650, 950 450
          C 1000 250, 1150 150, 1500 100
          Z
        "
        fill="url(#vineGrad2)"
      />
    </svg>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [demoLinkId, setDemoLinkId] = useState(null);

  useEffect(() => {
    setVisible(true);
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowScrollTop(window.scrollY > 600);
    };
    window.addEventListener("scroll", onScroll);
    // Hent demo-link med retry (op til 3 forsøg)
    let retries = 0;
    const fetchDemoLink = () => {
      axios.get(`${API}/demo-links/landing`).then(res => {
        if (res.data?.id) setDemoLinkId(res.data.id);
      }).catch((err) => {
        console.warn('Demo-link fetch fejl:', err?.response?.status, err?.message);
        if (retries < 3) {
          retries++;
          setTimeout(fetchDemoLink, 2000 * retries);
        }
      });
    };
    fetchDemoLink();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollToFeatures = () =>
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#F0EBE3] text-[#2C2416] overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* ─── NAVBAR ─── */}
      <nav
        data-testid="landing-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#F0EBE3]/90 backdrop-blur-lg shadow-sm border-b border-[#D4C9B8]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <ResoviaLogo className="w-10 h-10" tone="dark" />
            <span className="font-heading text-2xl font-bold uppercase tracking-tight text-[#2C2416]">
              Resovia
            </span>
          </div>
          <Link to="/login" data-testid="navbar-login-btn">
            <Button className="bg-[#5B8C2A] hover:bg-[#4A7522] text-white font-medium px-6 py-2.5 rounded-md transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5">
              Log ind
            </Button>
          </Link>
        </div>
      </nav>

      {/* ═══ FLOWING SECTIONS WRAPPER ═══ */}
      <div className="relative">
        {/* ONE continuous organic SVG vine spanning hero + features + values */}
        <FlowingVine />

        {/* ─── HERO ─── */}
        <section
          data-testid="landing-hero"
          className="relative min-h-screen flex items-center pt-20"
        >
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl">
              <div
                className={`transition-all duration-700 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#5B8C2A]/15 text-[#5B8C2A] text-sm font-medium mb-6 border border-[#5B8C2A]/20">
                  <Leaf className="w-4 h-4" />
                  Festival Logistics Management
                </span>

                <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter text-[#2C2416] leading-[0.9] mb-6">
                  Overblik.
                  <br />
                  <span className="text-[#5B8C2A]">Kontrol.</span>
                  <br />
                  Bæredygtighed.
                </h1>

                <p className="text-lg sm:text-xl text-[#6B5D4D] leading-relaxed max-w-lg mb-10">
                  Resovia samler ressourcehåndtering, affaldssporing, kommunikation og
                  dataindsamling i ét intuitivt system — så dit event kører gnidningsfrit.
                </p>

                <div className="flex flex-wrap gap-4">
                  <Link to="/login" data-testid="hero-login-btn">
                    <Button className="bg-[#5B8C2A] hover:bg-[#4A7522] text-white font-medium text-base px-8 py-3.5 rounded-md transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 gap-2">
                      Kom i gang
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  {demoLinkId && (
                    <Button
                      onClick={() => navigate(`/demo/${demoLinkId}`)}
                      className="bg-[#2C2416] hover:bg-[#3D3324] text-white font-medium text-base px-8 py-3.5 rounded-md transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 gap-2 border border-[#5B8C2A]/30"
                      data-testid="hero-demo-btn"
                    >
                      <Play className="w-4 h-4" />
                      Prøv Demo
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={scrollToFeatures}
                    className="text-[#6B5D4D] hover:text-[#2C2416] hover:bg-[#D4C9B8]/40 font-medium text-base px-6 py-3.5 rounded-md gap-2"
                    data-testid="hero-learn-more-btn"
                  >
                    Se funktioner
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs text-[#8B7D6B] font-medium tracking-wider uppercase">Scroll</span>
            <ChevronDown className="w-5 h-5 text-[#8B7D6B]" />
          </div>
        </section>

        {/* ─── FEATURES ─── */}
        <section
          id="features"
          data-testid="landing-features"
          className="relative py-24 lg:py-32"
        >
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="font-mono text-sm text-[#5B8C2A] tracking-wider uppercase">
                Funktioner
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#2C2416] mt-3">
                Alt du behøver — ét sted
              </h2>
              <p className="text-[#6B5D4D] mt-4 max-w-2xl mx-auto text-base sm:text-lg">
                Fra ressourcekort til analyse — Resovia giver dig de værktøjer, der gør
                dit event mere organiseret, effektivt og bæredygtigt.
              </p>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  data-testid={`feature-card-${i}`}
                  className="group relative bg-[#FAF7F2]/90 backdrop-blur-sm border border-[#D4C9B8] hover:bg-[#2C2416] rounded-xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-default"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#5B8C2A]/15 group-hover:bg-[#5B8C2A]/25 flex items-center justify-center mb-5 transition-colors duration-300">
                    <f.icon className="w-6 h-6 text-[#5B8C2A] group-hover:text-[#88C057] transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[#2C2416] group-hover:text-white mb-3 transition-colors duration-300">
                    {f.title}
                  </h3>
                  <p className="text-[#6B5D4D] group-hover:text-[#C8BDAE] leading-relaxed text-sm transition-colors duration-300">
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── VALUES ─── */}
        <section data-testid="landing-values" className="relative py-24 lg:py-32">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="font-mono text-sm text-[#5B8C2A] tracking-wider uppercase">
                  Hvorfor Resovia?
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#2C2416] mt-3 mb-6">
                  Skabt til dem, der <br className="hidden sm:block" />
                  skaber oplevelser
                </h2>
                <p className="text-[#6B5D4D] text-base sm:text-lg leading-relaxed mb-10">
                  Resovia er et logistikstyringssystem designet specifikt til festivaler
                  og events. Vi kombinerer opgavestyring, sporing og dataindsamling, så du
                  kan fokusere på at levere uforglemmelige oplevelser — med overblik og
                  tryghed.
                </p>

                <div className="space-y-6">
                  {VALUES.map((v) => (
                    <div key={v.title} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#5B8C2A]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <v.icon className="w-5 h-5 text-[#5B8C2A]" />
                      </div>
                      <div>
                        <h4 className="font-heading text-base font-bold uppercase tracking-tight text-[#2C2416]">
                          {v.title}
                        </h4>
                        <p className="text-[#6B5D4D] text-sm mt-1">{v.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visual card */}
              <div className="hidden lg:flex justify-center">
                <div className="relative w-full max-w-md">
                  <div className="absolute top-6 left-6 right-0 bottom-0 bg-[#5B8C2A]/10 border border-[#5B8C2A]/20 rounded-2xl" />
                  <div className="relative bg-[#FAF7F2]/95 backdrop-blur-sm border border-[#D4C9B8] rounded-2xl p-8 shadow-xl">
                    <div className="flex items-center gap-3 mb-6">
                      <ResoviaLogo className="w-10 h-10" tone="dark" />
                      <div>
                        <div className="font-heading text-lg font-bold uppercase text-[#2C2416]">Resovia</div>
                        <div className="text-xs text-[#8B7D6B] font-mono">Festival Logistics</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-[#E8E0D4] rounded-lg p-4">
                        <div className="text-2xl font-bold font-mono text-[#5B8C2A]">24</div>
                        <div className="text-xs text-[#8B7D6B]">Aktive zoner</div>
                      </div>
                      <div className="bg-[#E8E0D4] rounded-lg p-4">
                        <div className="text-2xl font-bold font-mono text-[#2C2416]">156</div>
                        <div className="text-xs text-[#8B7D6B]">Frivillige</div>
                      </div>
                      <div className="bg-[#E8E0D4] rounded-lg p-4">
                        <div className="text-2xl font-bold font-mono text-[#2C2416]">98%</div>
                        <div className="text-xs text-[#8B7D6B]">Dækning</div>
                      </div>
                      <div className="bg-[#E8E0D4] rounded-lg p-4">
                        <div className="text-2xl font-bold font-mono text-[#5B8C2A]">12t</div>
                        <div className="text-xs text-[#8B7D6B]">Sparet/uge</div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-2">
                        <span className="text-[#8B7D6B]">Ressource-allokering</span>
                        <span className="font-mono text-[#5B8C2A] font-medium">87%</span>
                      </div>
                      <div className="h-2 bg-[#E8E0D4] rounded-full overflow-hidden">
                        <div className="h-full bg-[#5B8C2A] rounded-full" style={{ width: "87%" }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* ═══ END FLOWING WRAPPER ═══ */}

      {/* ─── CTA ─── */}
      <section data-testid="landing-cta" className="py-24 lg:py-32 bg-[#2C2416]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tighter text-white mb-6">
            Klar til at tage kontrol?
          </h2>
          <p className="text-[#A89A87] text-base sm:text-lg max-w-xl mx-auto mb-10">
            Log ind og oplev, hvordan Resovia kan transformere logistikken for dit
            næste event.
          </p>
          <Link to="/login" data-testid="cta-login-btn">
            <Button className="bg-[#5B8C2A] hover:bg-[#4A7522] text-white font-medium text-base px-10 py-4 rounded-md transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 gap-2">
              Log ind nu
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section data-testid="landing-contact" className="py-16 bg-[#E8E0D4] border-t border-[#D4C9B8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-mono text-sm text-[#5B8C2A] tracking-wider uppercase">Kontakt</span>
          <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#2C2416] mt-2 mb-4">
            Har du spørgsmål?
          </h3>
          <a
            href="mailto:kontakt@resovia.dk"
            data-testid="contact-email-link"
            className="inline-flex items-center gap-2 text-[#6B5D4D] hover:text-[#5B8C2A] transition-colors text-base"
          >
            <Mail className="w-5 h-5" />
            kontakt@resovia.dk
          </a>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer data-testid="landing-footer" className="bg-[#2C2416] text-[#A89A87] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ResoviaLogo className="w-8 h-8" tone="light" />
            <span className="font-heading text-lg font-bold uppercase tracking-tight text-white">Resovia</span>
          </div>
          <p className="text-sm">&copy; 2026 Resovia. Alle rettigheder forbeholdes.</p>
        </div>
      </footer>

      {/* ─── SCROLL TO TOP ─── */}
      <button
        data-testid="scroll-to-top-btn"
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-[#5B8C2A] hover:bg-[#4A7522] text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 ${
          showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label="Scroll til toppen"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </div>
  );
}
